import React from 'react';
import { FormControl, FormLabel, Grid, IconButton, Stack, Tooltip } from '@mui/material';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import { format, parseISO } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { OwnerAutocomplete } from '@/components/selects/OwnerSelect';
import { AuditActivityTypeSelect } from '@/components/selects/AuditActivityTypeSelect';
import { InstantClientSelect } from '@/components/selects/ClientSelect';
import { PresetDateRangeSelect } from '@/components/selects/PresetDateRangeSelect';
import { useAuditUserFilters } from '@/hooks/useAuditUserFilters';
import {
	setActionTypes,
	setClientId,
	setClientName,
	setFromDate,
	setToDate,
	setUserIds,
	resetFilters,
} from '@/features/table/auditTableSlice';
import { useGridItemProps } from '@/components/table/filterGrid';

export const AuditTableFilters = ({ disabled = false }) => {
	const dispatch = useDispatch();
	const filters = useSelector((state) => state.auditTablePrefs);
	const { userId, canViewGroup, userIds } = useAuditUserFilters();

	const { searchGridProps, generalGridProps, clearFilterGridProps } = useGridItemProps();

	return (
		<Grid
			container
			spacing={1}
			sx={{
				width: '100%',
				pb: 2,
			}}
		>
			<Grid
				container
				spacing={2}
				width='100%'
				sx={{
					'& .MuiFormControl-root': {
						width: '100%',
					},
				}}
			>
				<Grid {...searchGridProps}>
					<Stack direction='column' width='100%'>
						<FormLabel>Associated client</FormLabel>
						<InstantClientSelect
							clientId={filters.clientId}
							clientName={filters.clientName}
							disabled={disabled}
							setClient={(client) => {
								dispatch(setClientId(client?.id ?? null));
								dispatch(setClientName(client?.name ?? null));
							}}
						/>
					</Stack>
				</Grid>
				<Grid {...generalGridProps}>
					<FormControl fullWidth>
						<FormLabel>Date range</FormLabel>
						<PresetDateRangeSelect
							disabled={disabled}
							from={filters.fromDate ? parseISO(filters.fromDate) : null}
							to={filters.toDate ? parseISO(filters.toDate) : null}
							onChange={(nextFrom, nextTo) => {
								dispatch(setFromDate(nextFrom ? format(nextFrom, 'yyyy-MM-dd') : null));
								dispatch(setToDate(nextTo ? format(nextTo, 'yyyy-MM-dd') : null));
							}}
						/>
					</FormControl>
				</Grid>
				<Grid {...generalGridProps}>
					<FormControl fullWidth>
						<FormLabel>User</FormLabel>
						<OwnerAutocomplete includeInactive
							multiple
							userId={userIds}
							disabled={disabled || !canViewGroup}
							isUserFixed={(user) => {
								if (user.id === userId) return userIds.length === 1;
								return false;
							}}
							onChange={(value) => {
								const newIds = [value].flat();
								dispatch(setUserIds(newIds.length === 0 ? [] : newIds));
							}}
							returnValue='id'
							showGroup
							disableClearable
						/>
					</FormControl>
				</Grid>
				<Grid {...generalGridProps}>
					<Stack direction='column' sx={{ width: '100%' }}>
						<AuditActivityTypeSelect
							value={filters.actionTypes ?? []}
							disabled={disabled}
							onChange={(actionTypes) => dispatch(setActionTypes(actionTypes))}
						/>
					</Stack>
				</Grid>
				<Grid {...clearFilterGridProps}>
					<Tooltip title='Clear filters'>
						<IconButton
							variant='text'
							size='small'
							color='primary'
							disabled={disabled}
							onClick={() => dispatch(resetFilters())}
							aria-label='Clear filters'
							sx={{ mb: 3 }}
						>
							<FilterAltOffOutlinedIcon />
						</IconButton>
					</Tooltip>
				</Grid>
			</Grid>
		</Grid>
	);
};
